import { raidAuthoringApi } from "../../services/raidAuthoringApi";
import { referenceApi } from "../../services/api";
import "./ResourceTables.css";
import {
  buildConditionTree as restoreConditionTree,
  readReference,
} from "../../utils/raidAuthoring";
// V3 트리거 시스템을 위한 새로운 트리거 탭
import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Card,
  message,
  Popconfirm,
  Typography,
  Badge,
  Empty,
  Tabs,
  Input,
  Select,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useApp } from "../../contexts/AppContext";
import { TriggerEditorV3 } from "../common/TriggerEditorV3";
import { supabase } from "../../lib/supabase";

const { Title, Text } = Typography;
const fallbackEventLabels: Record<string, string> = {
  ON_BATTLE_START: "전투 시작",
  ON_PERIODIC_TIMER: "주기 타이머",
  ON_DAMAGE_TAKEN: "피해를 받았을 때",
  ON_SKILL_FINISH: "스킬 종료",
  ON_PROPERTY_CHANGE: "속성 변경",
  ON_SKILL_START: "스킬 시작",
  ON_DEATH: "사망",
};
const { TabPane } = Tabs;

interface TriggersTabV3Props {
  bossCode?: string;
  entityCode?: string;
  onUpdate?: () => void;
}

interface TriggerV3 {
  id: string;
  trigger_code: string;
  trigger_name: string;
  entity_code: string;
  event_type: string;
  event_params: any;
  event_participants: any;
  is_enabled: boolean;
  is_repeatable: boolean;
  max_trigger_count: number;
  current_trigger_count: number;
  priority: number;
  description: string;
  created_at: string;
  updated_at: string;
}

interface TriggerConditionGroupV3 {
  id: string;
  trigger_id: string;
  group_name: string;
  logic_operator: "AND" | "OR";
  created_at: string;
  updated_at: string;
}

interface TriggerConditionV3 {
  id: string;
  group_id: string;
  nested_group_id?: string;
  participant_role_code?: string;
  value_type_code?: string;
  operator_code?: string;
  compare_value?: string;
  custom_property_name?: string;
  effect_code?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface TriggerActionV3 {
  id: string;
  trigger_id: string;
  action_order: number;
  action_type: string;
  target_role: string;
  action_params: any;
  delay_seconds: number;
}

const TriggersTabV3: React.FC<TriggersTabV3Props> = ({
  bossCode,
  entityCode,
  onUpdate,
}) => {
  const { state } = useApp();
  const [search, setSearch] = useState("");
  const [eventLabels, setEventLabels] = useState(fallbackEventLabels);
  useEffect(() => {
    let current = true;
    referenceApi
      .fetchEventTypes()
      .then((rows) => {
        if (current)
          setEventLabels({
            ...fallbackEventLabels,
            ...Object.fromEntries(
              rows.map((r: any) => [r.event_code, r.event_name]),
            ),
          });
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, []);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [triggers, setTriggers] = useState<TriggerV3[]>([]);
  const [selectedTrigger, setSelectedTrigger] = useState<TriggerV3 | null>(
    null,
  );
  const [conditionGroups, setConditionGroups] = useState<
    Record<string, TriggerConditionGroupV3[]>
  >({});
  const [conditions, setConditions] = useState<
    Record<string, TriggerConditionV3[]>
  >({});
  const [actions, setActions] = useState<Record<string, TriggerActionV3[]>>({});
  const [tableExists, setTableExists] = useState<boolean | null>(null);

  // 현재 엔티티 코드
  const currentEntityCode =
    entityCode || bossCode || state.selectedBoss?.boss_code;

  // 테이블 존재 여부 확인
  const checkTableExists = async () => {
    try {
      const { data, error } = await supabase
        .from("trigger_definition_v3")
        .select("id")
        .limit(1);

      if (error && error.code === "42P01") {
        console.error("❌ V3 테이블이 존재하지 않습니다!");
        setTableExists(false);
        return false;
      }

      console.log("✅ V3 테이블 확인됨");
      setTableExists(true);
      return true;
    } catch (err) {
      console.error("❌ V3 테이블 확인 중 오류:", err);
      setTableExists(false);
      return false;
    }
  };

  // V3 트리거 목록 로드
  const loadTriggersV3 = async () => {
    if (!currentEntityCode) {
      return;
    }

    setLoading(true);
    try {
      // 부모 엔티티의 트리거 로드
      const { data: parentTriggers, error: triggersError } = await supabase
        .from("trigger_definition_v3")
        .select("*")
        .eq("entity_code", currentEntityCode)
        .order("priority", { ascending: false });

      if (triggersError) {
        console.error("❌ V3 Query error:", triggersError);
        throw triggersError;
      }

      // 자식 엔티티 확인
      const { data: childEntities } = await supabase
        .from("raid_entity_renewed")
        .select("entity_code, entity_name")
        .eq("parent_code", currentEntityCode);

      // 자식 엔티티의 트리거 로드
      let allTriggers = [...(parentTriggers || [])];
      if (!entityCode && childEntities && childEntities.length > 0) {
        for (const child of childEntities) {
          const { data: childTriggers } = await supabase
            .from("trigger_definition_v3")
            .select("*")
            .eq("entity_code", child.entity_code)
            .order("priority", { ascending: false });

          if (childTriggers && childTriggers.length > 0) {
            // 자식 트리거에 부모 정보 추가
            childTriggers.forEach((trigger) => {
              (trigger as any)._parent_entity = child.entity_code;
              (trigger as any)._parent_name = child.entity_name;
            });

            allTriggers = [...allTriggers, ...childTriggers];
          }
        }
      }

      setTriggers(allTriggers);

      // 각 트리거의 조건 그룹, 조건, 액션 로드
      if (allTriggers && allTriggers.length > 0) {
        const triggerIds = allTriggers.map((t) => t.id);

        // 그룹화를 위한 변수들 먼저 선언
        const groupsByTrigger: Record<string, TriggerConditionGroupV3[]> = {};
        const conditionsByTrigger: Record<string, TriggerConditionV3[]> = {};
        const actionsByTrigger: Record<string, TriggerActionV3[]> = {};

        // 조건 그룹 로드
        console.log("🔍 V3 조건 그룹 로드 시작...");
        const { data: groupsData, error: groupsError } = await supabase
          .from("boss_trigger_condition_group_v3")
          .select("*")
          .in("trigger_id", triggerIds);

        if (groupsError) {
          console.error("❌ V3 그룹 로드 오류:", groupsError);
          throw groupsError;
        }
        console.log("✅ V3 그룹 로드 성공:", groupsData?.length || 0, "개");

        // 조건 로드
        if (groupsData && groupsData.length > 0) {
          const groupIds = groupsData.map((g) => g.id);
          console.log("🔍 V3 조건 항목 로드 시작...");
          const { data: conditionsData, error: conditionsError } =
            await supabase
              .from("boss_trigger_condition_v3")
              .select("*")
              .in("group_id", groupIds)
              .order("sort_order");

          if (conditionsError) {
            console.error("❌ V3 조건 로드 오류:", conditionsError);
            throw conditionsError;
          }
          console.log(
            "✅ V3 조건 로드 성공:",
            conditionsData?.length || 0,
            "개",
          );

          // 조건 데이터를 각 트리거별로 그룹화
          conditionsData?.forEach((cond) => {
            const group = groupsData?.find((g) => g.id === cond.group_id);
            if (group) {
              const triggerId = group.trigger_id;
              if (!conditionsByTrigger[triggerId]) {
                conditionsByTrigger[triggerId] = [];
              }
              conditionsByTrigger[triggerId].push(cond);
            }
          });

          setConditions(conditionsByTrigger);
        } else {
          setConditions({});
        }

        // 액션 로드
        console.log("🔍 V3 액션 로드 시작...");
        const { data: actionsData, error: actionsError } = await supabase
          .from("trigger_action_v3")
          .select("*")
          .in("trigger_id", triggerIds)
          .order("action_order");

        if (actionsError) {
          console.error("❌ V3 액션 로드 오류:", actionsError);
          throw actionsError;
        }
        console.log("✅ V3 액션 로드 성공:", actionsData?.length || 0, "개");

        // 첫 번째 액션의 구조 확인
        if (actionsData && actionsData.length > 0) {
          console.log("📋 첫 번째 액션 예시:", actionsData[0]);
          console.log("📋 액션 파라미터:", actionsData[0].action_params);
        }

        // 그룹 데이터 그룹화
        groupsData?.forEach((group) => {
          if (!groupsByTrigger[group.trigger_id]) {
            groupsByTrigger[group.trigger_id] = [];
          }
          groupsByTrigger[group.trigger_id].push(group);
        });

        // 액션 데이터 그룹화
        actionsData?.forEach((action) => {
          if (!actionsByTrigger[action.trigger_id]) {
            actionsByTrigger[action.trigger_id] = [];
          }
          actionsByTrigger[action.trigger_id].push(action);
        });

        setConditionGroups(groupsByTrigger);
        setActions(actionsByTrigger);
      }
    } catch (error) {
      console.error("Error loading V3 triggers:", error);
      message.error("V3 트리거 로드 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 트리거 삭제
  const handleDeleteTrigger = async (triggerId: string) => {
    try {
      const { error } = await supabase
        .from("trigger_definition_v3")
        .delete()
        .eq("id", triggerId);

      if (error) throw error;

      message.success("트리거가 삭제되었습니다.");
      loadTriggersV3();
      window.dispatchEvent(new Event("triggersUpdated"));
      onUpdate?.();
    } catch (error) {
      console.error("Error deleting trigger:", error);
      message.error("트리거 삭제 중 오류가 발생했습니다.");
    }
  };

  // 트리거 저장 (복합 조건 시스템 지원)
  const handleSaveTrigger = async (
    trigger: any,
    conditionData: any,
    triggerActions: any[],
  ) => {
    try {
      await raidAuthoringApi.saveTrigger(
        trigger,
        conditionData,
        triggerActions,
        selectedTrigger?.updated_at || null,
      );
      message.success("트리거가 저장되었습니다.");
      setShowEditor(false);
      setSelectedTrigger(null);
      loadTriggersV3();
      window.dispatchEvent(new Event("triggersUpdated"));
      onUpdate?.();
    } catch (error) {
      console.error("Error saving trigger:", error);
      throw error; // Keep the editor open and show the precise error.
    }
  };

  // 복합 조건 트리 구조 재구성
  const buildConditionTree = (triggerId: string) => {
    return restoreConditionTree(
      conditionGroups[triggerId] || [],
      conditions[triggerId] || [],
    );
  };

  // 자연어 설명 생성
  const generateNaturalDescription = (
    trigger: TriggerV3,
    triggerGroups: TriggerConditionGroupV3[],
    triggerConditions: TriggerConditionV3[],
    triggerActions: TriggerActionV3[],
  ) => {
    let desc = `[${trigger.entity_code}]가 `;

    // 이벤트
    const eventMap: Record<string, string> = {
      ON_BATTLE_START: "전투 시작 시",
      ON_PERIODIC_TIMER: "주기적으로",
      ON_HP_THRESHOLD: "HP 임계값 도달 시",
      ON_DAMAGE_TAKEN: "피해를 입었을 때",
      ON_SKILL_START: "스킬 시작 시",
      ON_SKILL_FINISH: "스킬 완료 시",
      ON_PROPERTY_CHANGE: "속성 변경 시",
      ON_PLAYER_DEATH: "플레이어 사망 시",
    };

    desc += eventMap[trigger.event_type] || trigger.event_type;

    // event_params 파싱 및 표시
    if (trigger.event_params) {
      if (trigger.event_params.interval) {
        desc += ` (${trigger.event_params.interval}초마다)`;
      }
      if (readReference(trigger.event_params, "skill")) {
        desc += ` [스킬: ${readReference(trigger.event_params, "skill")}]`;
      }
      if (trigger.event_params.property) {
        desc += ` [속성: ${trigger.event_params.property}]`;
      }
      if (trigger.event_params.result) {
        desc += ` [결과: ${trigger.event_params.result}]`;
      }
    }

    // event_participants 파싱 및 표시
    if (trigger.event_participants) {
      const parts = [];
      if (trigger.event_participants.listener) {
        parts.push(`리스너: ${trigger.event_participants.listener}`);
      }
      if (trigger.event_participants.event_source) {
        parts.push(`소스: ${trigger.event_participants.event_source}`);
      }
      if (trigger.event_participants.event_target) {
        parts.push(`타겟: ${trigger.event_participants.event_target}`);
      }
      if (parts.length > 0) {
        desc += ` (${parts.join(", ")})`;
      }
    }

    // 복합 조건 (트리 구조로 표현)
    let conditionTree;
    try {
      conditionTree = buildConditionTree(trigger.id);
    } catch (e: any) {
      return desc + "\n조건 오류: " + e.message;
    }
    if (conditionTree) {
      desc += "\n조건: ";
      const renderConditionTree = (node: any, depth: number = 0): string => {
        if (node.type === "CONDITION") {
          let condStr = `${node.participant_role_code}의 `;

          // value_type_code에 따른 표시
          if (
            node.value_type_code === "CUSTOM_PROPERTY" &&
            node.custom_property_name
          ) {
            condStr += `${node.custom_property_name} `;
          } else if (
            node.value_type_code === "EFFECT_STACK" &&
            node.effect_code
          ) {
            condStr += `${node.effect_code} 스택 `;
          } else {
            condStr += `${node.value_type_code} `;
          }

          condStr += `${node.operator_code} ${node.compare_value}`;
          return condStr;
        } else {
          const indent = "  ".repeat(depth);
          const childStrs = node.children.map((child: any) =>
            renderConditionTree(child, depth + 1),
          );
          return depth === 0
            ? childStrs.join(` ${node.logic_operator} `)
            : `(${childStrs.join(` ${node.logic_operator} `)})`;
        }
      };
      desc += renderConditionTree(conditionTree);
    }

    // 액션
    if (triggerActions.length > 0) {
      desc += "\n액션: ";
      desc += triggerActions
        .map((action, i) => {
          const delay =
            action.delay_seconds > 0 ? ` (${action.delay_seconds}초 후)` : "";
          let actionDetail = `${action.action_type} → ${action.target_role}${delay}`;

          // 액션 파라미터 상세 표시
          if (action.action_params) {
            console.log(
              `📋 Action params for ${action.action_type}:`,
              action.action_params,
            );

            if (action.action_type === "APPLY_EFFECT") {
              if (action.action_params.effect_id) {
                actionDetail += ` [효과: ${action.action_params.effect_id}]`;
              }
              if (action.action_params.duration) {
                actionDetail += ` [지속: ${action.action_params.duration}초]`;
              }
            } else if (action.action_type === "CAST_SKILL") {
              if (action.action_params.skill_id) {
                actionDetail += ` [스킬: ${action.action_params.skill_id}]`;
              }
            } else if (action.action_type === "MANIPULATE_PROPERTY") {
              if (action.action_params.property) {
                actionDetail += ` [속성: ${action.action_params.property}]`;
              }
              if (action.action_params.operation) {
                actionDetail += ` [연산: ${action.action_params.operation}]`;
              }
              if (action.action_params.value !== undefined) {
                actionDetail += ` [값: ${action.action_params.value}]`;
              }
            } else if (action.action_type === "SHOW_MESSAGE") {
              if (action.action_params.message) {
                actionDetail += ` [메시지: "${action.action_params.message}"]`;
              }
            } else if (action.action_type === "SPAWN_ENTITY") {
              if (action.action_params.entity_code) {
                actionDetail += ` [엔티티: ${action.action_params.entity_code}]`;
              }
              if (action.action_params.count) {
                actionDetail += ` [수량: ${action.action_params.count}]`;
              }
            } else if (action.action_type === "REMOVE_EFFECT") {
              if (action.action_params.effect_id) {
                actionDetail += ` [효과: ${action.action_params.effect_id}]`;
              }
              if (action.action_params.all_stacks) {
                actionDetail += ` [모든 스택]`;
              }
            } else if (action.action_type === "END_COMBAT") {
              if (action.action_params.result) {
                actionDetail += ` [결과: ${action.action_params.result}]`;
              }
            }
          }

          return `\n  ${i + 1}. ${actionDetail}`;
        })
        .join("");
    }

    return desc;
  };

  // 컬럼 정의
  const columns: ColumnsType<TriggerV3> = [
    {
      title: "트리거 이름",
      dataIndex: "trigger_name",
      key: "trigger_name",
      width: 250,
      render: (name: string, record) => {
        return (
          <Space>
            <span>{name}</span>
            {(record as any)._parent_entity && (
              <Tag color="purple" style={{ fontSize: "11px" }}>
                {(record as any)._parent_name || (record as any)._parent_entity}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "이벤트",
      dataIndex: "event_type",
      key: "event_type",
      width: 150,
      render: (eventType: string) => {
        const eventMap: Record<
          string,
          { color: string; icon: React.ReactNode }
        > = {
          ON_BATTLE_START: { color: "green", icon: <ThunderboltOutlined /> },
          ON_PERIODIC_TIMER: { color: "blue", icon: <ThunderboltOutlined /> },
          ON_DAMAGE_TAKEN: { color: "red", icon: <ThunderboltOutlined /> },
          ON_SKILL_FINISH: { color: "purple", icon: <ThunderboltOutlined /> },
          ON_PROPERTY_CHANGE: { color: "orange", icon: <ExperimentOutlined /> },
        };

        const config = eventMap[eventType] || { color: "default", icon: null };
        return (
          <Tag color={config.color} icon={config.icon}>
            {eventLabels[eventType] || eventType}
          </Tag>
        );
      },
    },
    {
      title: "상태",
      key: "status",
      width: 120,
      render: (_, record) => (
        <Space>
          {record.is_enabled ? (
            <Badge status="success" text="활성" />
          ) : (
            <Badge status="default" text="비활성" />
          )}
          {record.is_repeatable && <Tag color="cyan">반복</Tag>}
        </Space>
      ),
    },
    {
      title: "우선순위",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: "관리",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            aria-label={`${record.trigger_name} 편집`}
            icon={<EditOutlined />}
            onClick={() => {
              try {
                buildConditionTree(record.id);
              } catch (e: any) {
                message.error(e.message);
                return;
              }
              setSelectedTrigger(record);
              setShowEditor(true);
            }}
          />
          <Popconfirm
            title="이 트리거를 삭제하시겠습니까?"
            onConfirm={() => handleDeleteTrigger(record.id)}
            okText="삭제"
            cancelText="취소"
          >
            <Button
              size="small"
              danger
              aria-label={`${record.trigger_name} 삭제`}
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    console.log("🎯 V3 useEffect triggered, entityCode:", currentEntityCode);

    // 먼저 테이블 존재 여부 확인
    checkTableExists().then((exists) => {
      if (exists && currentEntityCode) {
        loadTriggersV3();
      } else if (!exists) {
        console.error("❌ V3 테이블이 없어서 로드를 건너뜁니다");
      }
    });
  }, [currentEntityCode]);

  // 전체 트리거 개수 확인 (디버깅용)
  useEffect(() => {
    const checkTotalTriggers = async () => {
      const { data, error } = await supabase
        .from("trigger_definition_v3")
        .select("entity_code, id")
        .order("entity_code");

      if (data && data.length > 0) {
        const entityGroups = data.reduce(
          (acc, curr) => {
            acc[curr.entity_code] = (acc[curr.entity_code] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        );

        console.log("📈 V3 전체 트리거 현황:", {
          total: data.length,
          byEntity: entityGroups,
        });
      } else {
        console.log("⚠️ V3 테이블에 데이터가 없습니다");
      }
    };

    if (tableExists) {
      checkTotalTriggers();
    }
  }, [tableExists]);

  if (!currentEntityCode) {
    return (
      <Card>
        <Empty description="보스를 선택해주세요" />
      </Card>
    );
  }

  if (tableExists === false) {
    return (
      <Card>
        <Empty
          description={
            <div>
              <p>V3 트리거 시스템 테이블이 없습니다.</p>
              <p>Supabase에서 다음 파일들을 실행해주세요:</p>
              <ul style={{ textAlign: "left" }}>
                <li>001_create_inf_event_type.sql</li>
                <li>002_create_inf_participant_role.sql</li>
                <li>003_improve_condition_value_type.sql</li>
                <li>004_add_transition_operators_fixed_v2.sql</li>
                <li>005_add_generic_actions.sql</li>
                <li>006_deprecate_old_tables.sql</li>
                <li>007_update_main_schema_fixed.sql</li>
              </ul>
            </div>
          }
        />
      </Card>
    );
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              트리거
            </Title>

            <Tag color="blue">{triggers.length}개</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedTrigger(null);
                setShowEditor(true);
              }}
            >
              새 트리거 추가
            </Button>
          </Space>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            aria-label="트리거 검색"
            placeholder="이름 또는 코드 검색"
            value={search}
            allowClear
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
          />
          <Select
            aria-label="트리거 상태"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 140 }}
            options={[
              { value: "all", label: "모든 상태" },
              { value: "enabled", label: "활성" },
              { value: "disabled", label: "비활성" },
            ]}
          />
          <Text type="secondary">
            행을 펼치면 조건과 실행 순서를 확인할 수 있습니다.
          </Text>
        </Space>
        <Table
          className="resource-table"
          scroll={{ x: 850 }}
          columns={columns}
          dataSource={triggers.filter(
            (t) =>
              `${t.trigger_name} ${t.trigger_code} ${t.entity_code}`
                .toLowerCase()
                .includes(search.toLowerCase()) &&
              (statusFilter === "all" ||
                (statusFilter === "enabled" ? t.is_enabled : !t.is_enabled)),
          )}
          rowKey="id"
          loading={loading}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: "16px" }}>
                <p>
                  <Text code>{record.trigger_code}</Text> ·{" "}
                  <Text type="secondary">{record.entity_code}</Text>
                </p>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {generateNaturalDescription(
                    record,
                    conditionGroups[record.id] || [],
                    conditions[record.id] || [],
                    actions[record.id] || [],
                  )}
                </pre>
                {record.description && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">설명: {record.description}</Text>
                  </div>
                )}
              </div>
            ),
          }}
          pagination={{
            defaultPageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ["15", "30", "60", "100"],
            showTotal: (total) => `총 ${total}개`,
          }}
          locale={{ emptyText: "조건에 맞는 트리거가 없습니다" }}
        />
      </Card>

      {/* 트리거 에디터 모달 */}
      {showEditor && (
        <TriggerEditorV3
          entityCode={selectedTrigger?.entity_code || currentEntityCode}
          triggerId={selectedTrigger?.id}
          onSave={handleSaveTrigger}
          onClose={() => {
            setShowEditor(false);
            setSelectedTrigger(null);
          }}
          initialData={
            selectedTrigger
              ? {
                  trigger: selectedTrigger,
                  conditionGroups: buildConditionTree(selectedTrigger.id),
                  actions: actions[selectedTrigger.id] || [],
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default TriggersTabV3;
